/**
 * Headless presentation helper for the dictionary admin surface's "used by"
 * section (design doc
 * docs/features/product-catalogue/48_dictionary_admin_surface_design.md) —
 * turns one derivation's `Narrowing` into the human summary an operator
 * scans to see which slice of the matrix a catalogue item actually exposes
 * ("narrows Paper to 2 options; pins Size" / "no narrowing — pure re-badge").
 */

import type { UsedByEntry } from "../data/api-types";

type Narrowing = UsedByEntry["narrowing"];

/** Field keys arrive in whatever case the catalogue definition happens to
 *  use internally ("cover_stock", "size") — never the raw internal name in
 *  a sentence an operator reads. "Cover stock", "Size". */
function humanizeFieldKey(key: string): string {
  const spaced = key.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function summarizeNarrowing(narrowing: Narrowing | undefined): string {
  const fields = narrowing?.fields ?? {};
  const parts: string[] = [];

  for (const [key, fieldNarrowing] of Object.entries(fields)) {
    const label = humanizeFieldKey(key);
    if (fieldNarrowing.pinned) {
      parts.push(`pins ${label}`);
    } else if ((fieldNarrowing.allowed_options ?? []).length > 0) {
      const count = fieldNarrowing.allowed_options!.length;
      parts.push(
        `narrows ${label} to ${count} option${count === 1 ? "" : "s"}`,
      );
    }
  }

  const quantities = narrowing?.quantities ?? [];
  if (quantities.length > 0) {
    parts.push(
      `narrows quantities to ${quantities.length} break${quantities.length === 1 ? "" : "s"}`,
    );
  }

  return parts.length > 0 ? parts.join("; ") : "no narrowing — pure re-badge";
}
